import React, { useState, useCallback, useEffect, useRef, ReactNode, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import cn from 'classnames';
import pick from 'lodash/pick';
import { v4 as uuidv4 } from 'uuid';
import { isMobile } from 'react-device-detect';

import { setViewedWorkspaceItem, addItem } from 'Shared/actions/todos';
import { getDiscussionTopicByUuid } from 'Shared/selectors/workspace';
import { DropArea, useMobilePreviewDragLayer } from 'Shared/components/DragAndDropComponents';
import WorkspaceItemContainer from 'Shared/components/WorkspaceItemContainer/WorkspaceItemContainer';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import MobileContext from 'Shared/contexts/MobileContext';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { createEmptyEditorState, editorStateToJsonString } from 'Shared/utils/draftJsHelpers';
import UserPresenceInWorkspaceItem from 'Shared/components/UserPresenceInWorkspaceItem/UserPresenceInWorkspaceItem';

import { dragSpec, dropSpec } from './discussionTopicDragDrop';

import '@workpatterns/draft-js-mention-plugin/lib/plugin.css';
import 'draft-js/dist/Draft.css';
import './DiscussionTopic.scss';

const neededItemFields = Object.freeze([
  'uuid',
  'id',
  'video',
  'parentUuid',
  'itemType',
  'workspaceId',
  'completed',
  'isHighPriority',
]);

type Props = {
  isSection?: boolean;
  isDisabled?: boolean;
  isCompleteToggleDisabled?: boolean;
  parentIsSection?: boolean;
  uuid: string;
  children?: any;
  selector?: string;
  enableHideAnimation?: boolean;
  isFirst: boolean;
  isFocused: boolean;
  showPresenceComponent?: boolean;
  parentItemInfoComponent?: ReactNode;
  notShowAsChild?: boolean;
  forbidDrag: boolean;
  onAddItem?: Function;
  nestItemByTabClick?: Function;
  onArrowUpClick: Function;
  onArrowDownClick: Function;
  onDrop?: Function;
  onNestByDrag?: Function;
  onFocusedTodoUpdate: Function;
  onHideAnimationTrigger?: Function;
  customActionsArea?: Function;
};

let hideAnimationRemoveTimeId;

const DiscussionTopic = React.memo(
  ({
    isSection = false,
    isDisabled = false,
    isCompleteToggleDisabled = false,
    enableHideAnimation = false,
    parentIsSection = false,
    uuid,
    children,
    isFocused,
    selector = 'workspace.discussion_topic',
    showPresenceComponent = false,
    parentItemInfoComponent = null,
    notShowAsChild = false,
    forbidDrag,
    onAddItem,
    nestItemByTabClick,
    onArrowUpClick,
    onArrowDownClick,
    onFocusedTodoUpdate,
    onDrop,
    onNestByDrag,
    customActionsArea,
  }: Props) => {
    const dispatch = useDispatch();
    const windowOfMobile = useContext(MobileContext);
    const { isSelected, notInGroupSelect, isNewItem } = useDeepEqualSelector(state => {
      const { selectedUuids } = state;
      return {
        notInGroupSelect: !selectedUuids.includes(uuid) || (selectedUuids.length === 1 && selectedUuids[0] === uuid),
        isSelected: Boolean(selectedUuids.find(selectedUuid => selectedUuid === uuid)),
        isNewItem: state.workspace.workspaceNotifications?.modifiedItemsUuids?.includes(uuid),
      };
    });
    const todoData = useDeepEqualSelector(state => pick(getDiscussionTopicByUuid(uuid)(state), neededItemFields));
    const { id: itemId, video, parentUuid, itemType, workspaceId, completed } = todoData;

    const [hoverOffset, setHoverOffset] = useState(null);
    const [isDropAsChild, setIsDropAsChild] = useState(null);
    const [isHideAnimationProccess, setHideAnimationProccess] = useState(false);

    const containerRef = useRef<HTMLDivElement>();

    const { mobilePreviewDragLayer, startShowMobilePreview, cancelShowMobilePreview } = useMobilePreviewDragLayer({
      ref: containerRef,
      itemUuid: uuid,
    });

    const [{ isDragging }, connectDragSource, dragPreview] = useDrag({
      item: { uuid, type: itemType },
      ...dragSpec({ uuid, forbidDrag, onDrop, onNestByDrag, dispatch, selector }),
    });
    const [{ isOver, canDrop }, connectDropTarget] = useDrop({
      accept: itemType,
      ...dropSpec({ uuid, containerRef, todoData, setHoverOffset, setIsDropAsChild }),
    });

    const isChildrenItemsPresented = Boolean(children);

    const hasUploadingVideo = video && !video.id;
    const isShowHideAnimation = enableHideAnimation && isHideAnimationProccess;

    useEffect(() => {
      dragPreview(getEmptyImage());
      return () => {
        clearTimeout(hideAnimationRemoveTimeId);
      };
    }, []);

    const handleStartAnimationProccess = useCallback(() => {
      if (hideAnimationRemoveTimeId) clearTimeout(hideAnimationRemoveTimeId);
      setHideAnimationProccess(true);
      hideAnimationRemoveTimeId = setTimeout(() => setHideAnimationProccess(false), 600);
    }, []);

    const handleClickAddSubTopic = useCallback(() => {
      const emptyEditorState = createEmptyEditorState();
      const emptyBody = editorStateToJsonString(emptyEditorState);
      const newUuid = uuidv4();

      dispatch(
        addItem({
          parentId: itemId,
          parentUuid: uuid,
          body: emptyBody,
          workspaceId,
          itemUuid: newUuid,
          itemType,
        })
      );

      setTimeout(() => {
        onFocusedTodoUpdate(newUuid);
        dispatch(updateSelectedUuids(newUuid, { isOneSelected: true }));
      }, 0);
    }, [itemId, uuid, itemType, dispatch, onFocusedTodoUpdate, workspaceId]);

    const handleItemClick = useCallback(() => {
      if (isNewItem) dispatch(setViewedWorkspaceItem(uuid));
    }, [isNewItem, uuid, dispatch]);

    return connectDropTarget(
      <div
        className={cn('DiscussionTopic position-relative', {
          'DiscussionTopic__drop--completed': completed && parentUuid && !parentItemInfoComponent,
          'd-none': isDragging,
        })}
        onClick={handleItemClick}
      >
        {mobilePreviewDragLayer}
        <div className="DiscussionTopic__left-drop position-absolute" />
        {Boolean(!hoverOffset) && isOver && canDrop && !isDropAsChild && (
          <DropArea isTop className={cn('', { 'mb-3': !parentUuid })} isDropAsChild={Boolean(parentUuid)} />
        )}

        <div
          className={cn('DiscussionTopic__container d-flex flex-column position-relative', {
            'DiscussionTopic__container--is-child': !!parentUuid && !notShowAsChild,
            'DiscussionTopic__container--is-top-level': !parentUuid || notShowAsChild,
          })}
          onTouchStart={startShowMobilePreview}
          onTouchMove={cancelShowMobilePreview}
          onTouchEnd={cancelShowMobilePreview}
          ref={containerRef}
        >
          <div
            ref={isMobile ? connectDragSource : null}
            className={cn('position-relative', {
              'is-hide-animation-proccess': isShowHideAnimation,
              rainbow: Math.floor(Math.random() * 10) === 5, // 1 in 10 chance of special rainbow class for animation
            })}
          >
            <div
              className={cn('DiscussionTopic__wrapper d-flex justify-content-start flex-column position-relative', {
                'DiscussionTopic__wrapper--skip-hover-coloring': isHideAnimationProccess,
                'DiscussionTopic__wrapper--is-selected': (isSelected || isFocused) && !isShowHideAnimation,
                'DiscussionTopic__wrapper--completed': completed,
                'DiscussionTopic__wrapper--is-first': !parentUuid || notShowAsChild,
                'DiscussionTopic__wrapper--has-childrens': isChildrenItemsPresented,
                'DiscussionTopic__wrapper--is-mobile': isMobile,
              })}
            >
              <div className="DiscussionTopic__editor-container d-flex position-relative">
                {showPresenceComponent && !windowOfMobile && <UserPresenceInWorkspaceItem itemUuid={uuid} />}
                {!hasUploadingVideo &&
                  !forbidDrag &&
                  !isMobile &&
                  connectDragSource(
                    <div className="DiscussionTopic__drag-mark position-absolute d-flex">
                      <WpIcon name="drag" prefix="fac" size="lg" />
                    </div>
                  )}

                <div className="DiscussionTopic__content-container d-flex flex-column position-relative">
                  <WorkspaceItemContainer
                    isDisabled={isDisabled}
                    notShowAsChild={notShowAsChild}
                    uuid={uuid}
                    isSelected={isSelected}
                    isFocused={isFocused}
                    parentIsSection={parentIsSection}
                    notInGroupSelect={notInGroupSelect}
                    onAddItem={onAddItem}
                    nestItemByTabClick={nestItemByTabClick}
                    onArrowUpClick={onArrowUpClick}
                    onArrowDownClick={onArrowDownClick}
                    onFocusedTodoUpdate={onFocusedTodoUpdate}
                    onHideAnimationTrigger={handleStartAnimationProccess}
                    customActionsArea={customActionsArea}
                    isCompleteToggleDisabled={isCompleteToggleDisabled}
                  />
                  <div className="todo-parent-info">{parentItemInfoComponent}</div>
                </div>
              </div>
            </div>
            {isChildrenItemsPresented && children}
            {isOver && canDrop && isDropAsChild && <DropArea isDropAsChild />}
          </div>
          {!parentUuid && !isDisabled && onAddItem && (
            <div
              className={cn('DiscussionTopic__add-btn py-1', {
                'DiscussionTopic__add-btn--is-section': isSection,
              })}
            >
              <WpButton variant="tertiary" size="sm" onClick={handleClickAddSubTopic}>
                <WpIcon name="plus-circle" />
                Add {isSection ? 'Topic' : 'Subtopic'}
              </WpButton>
            </div>
          )}
        </div>
        {Boolean(hoverOffset) && isOver && canDrop && !isDropAsChild && (
          <DropArea isBottom className={cn('', { 'mb-3': !parentUuid })} isDropAsChild={Boolean(parentUuid)} />
        )}
      </div>
    );
  }
);

export default DiscussionTopic;
